import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
    SERVICE_CASE_SELECT,
    type RegistrationCoverageRow,
    type ServiceCaseEventRow,
    type ServiceCasePhase,
    type ServiceCaseWithRelations,
} from '../components/service-cases/types';
import {
    groupTranscriptRows,
    type ServiceCaseTranscriptRow,
    type ServiceCaseTranscriptSession,
} from '../components/service-cases/chatTranscript';

export interface ServiceCaseListFilters {
    phase: string; // 'ALL' | ServiceCasePhase
    anchorKind: 'ALL' | 'job' | 'registration';
    manufacturerId: string; // 'ALL' | uuid
    hazard: 'ALL' | 'YES' | 'NO';
    coverageDecision: string; // 'ALL' | CoverageDecision
    search: string; // case number substring
}

export const DEFAULT_SERVICE_CASE_FILTERS: ServiceCaseListFilters = {
    phase: 'ALL',
    anchorKind: 'ALL',
    manufacturerId: 'ALL',
    hazard: 'ALL',
    coverageDecision: 'ALL',
    search: '',
};

export interface UseServiceCaseListParams {
    filters: ServiceCaseListFilters;
    page: number;
    rowsPerPage: number;
    /** Distinguishes the admin and manufacturer caches (RLS decides the rows). */
    scope: string;
    enabled?: boolean;
}

export interface ServiceCaseListResult {
    rows: ServiceCaseWithRelations[];
    totalCount: number;
}

/**
 * Server-side paginated list of service cases. RLS does the visibility filtering:
 * platform admins see everything, client members see cases whose `customer_id` is
 * their company.
 */
export function useServiceCaseList({
    filters,
    page,
    rowsPerPage,
    scope,
    enabled = true,
}: UseServiceCaseListParams) {
    return useQuery({
        queryKey: ['service-cases', scope, 'list', filters, page, rowsPerPage],
        queryFn: async (): Promise<ServiceCaseListResult> => {
            const from = page * rowsPerPage;
            const to = from + rowsPerPage - 1;

            let query = supabase
                .from('service_cases')
                .select(SERVICE_CASE_SELECT, { count: 'exact' });

            if (filters.phase !== 'ALL') {
                query = query.eq('phase', filters.phase as ServiceCasePhase);
            }
            if (filters.anchorKind === 'job') {
                query = query.not('original_job_id', 'is', null);
            } else if (filters.anchorKind === 'registration') {
                query = query.not('registration_id', 'is', null);
            }
            if (filters.manufacturerId !== 'ALL') {
                query = query.eq('manufacturer_id', filters.manufacturerId);
            }
            if (filters.hazard === 'YES') {
                query = query.eq('hazard_flag', true);
            } else if (filters.hazard === 'NO') {
                query = query.eq('hazard_flag', false);
            }
            if (filters.coverageDecision !== 'ALL') {
                query = query.eq('coverage_decision', filters.coverageDecision);
            }
            const search = filters.search.trim();
            if (search) {
                query = query.ilike('case_number', `%${search}%`);
            }

            const { data, count, error } = await query
                .order('updated_at', { ascending: false })
                .range(from, to);

            if (error) throw error;
            const rows = (data ?? []) as unknown as ServiceCaseWithRelations[];
            return { rows, totalCount: count ?? rows.length };
        },
        enabled,
    });
}

export function useServiceCase(caseId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'detail', caseId],
        queryFn: async (): Promise<ServiceCaseWithRelations | null> => {
            if (!caseId) return null;
            const { data, error } = await supabase
                .from('service_cases')
                .select(SERVICE_CASE_SELECT)
                .eq('id', caseId)
                .maybeSingle();
            if (error) throw error;
            return (data as unknown as ServiceCaseWithRelations | null) ?? null;
        },
        enabled: Boolean(caseId) && enabled,
    });
}

export function useServiceCaseEvents(caseId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'events', caseId],
        queryFn: async (): Promise<ServiceCaseEventRow[]> => {
            if (!caseId) return [];
            const { data, error } = await supabase.rpc('get_service_case_events', {
                p_case_id: caseId,
            });
            if (error) throw error;
            return (Array.isArray(data) ? data : []) as ServiceCaseEventRow[];
        },
        enabled: Boolean(caseId) && enabled,
    });
}

/**
 * The homeowner chat of a registration case (plan D8), read-only:
 * `get_service_case_chat_transcript(p_case_id)` (admins and the case's readers; job cases: none).
 */
export function useServiceCaseChatTranscript(caseId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'chat-transcript', caseId],
        queryFn: async (): Promise<ServiceCaseTranscriptSession[]> => {
            if (!caseId) return [];
            const { data, error } = await supabase.rpc('get_service_case_chat_transcript', {
                p_case_id: caseId,
            });
            if (error) throw error;
            return groupTranscriptRows(
                Array.isArray(data) ? (data as unknown as ServiceCaseTranscriptRow[]) : [],
            );
        },
        enabled: Boolean(caseId) && enabled,
    });
}

export function useRegistrationCoverage(registrationId: string | null | undefined) {
    return useQuery({
        queryKey: ['service-cases', 'registration-coverage', registrationId],
        queryFn: async (): Promise<RegistrationCoverageRow[]> => {
            if (!registrationId) return [];
            const { data, error } = await supabase.rpc('compute_registration_coverage', {
                p_registration_id: registrationId,
            });
            if (error) throw error;
            return (Array.isArray(data) ? data : []) as RegistrationCoverageRow[];
        },
        enabled: Boolean(registrationId),
    });
}

export interface ServiceCaseRmaReadiness {
    ready: boolean;
    reasons: string[];
}

/**
 * `service_case_rma_readiness(p_case_id)` → one (ready, reasons[]) row. Resolves to null when the
 * probe returns nothing usable, in which case the UI does not block and the server decides.
 */
export function useServiceCaseRmaReadiness(caseId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'rma-readiness', caseId],
        queryFn: async (): Promise<ServiceCaseRmaReadiness | null> => {
            if (!caseId) return null;
            const { data, error } = await supabase.rpc('service_case_rma_readiness', {
                p_case_id: caseId,
            });
            if (error) throw error;
            const row = Array.isArray(data) ? data[0] : null;
            if (!row || typeof row.ready !== 'boolean') return null;
            return { ready: row.ready, reasons: Array.isArray(row.reasons) ? row.reasons : [] };
        },
        enabled: Boolean(caseId) && enabled,
    });
}

/** Registration cases for the registration page (RLS: the manufacturer's members, admins). */
export function useRegistrationServiceCases(registrationId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'by-registration', registrationId],
        queryFn: async () => {
            if (!registrationId) return [];
            const { data, error } = await supabase
                .from('service_cases')
                .select('id, case_number, phase, coverage_decision, outcome, hazard_flag, created_at, closed_at')
                .eq('registration_id', registrationId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data ?? [];
        },
        enabled: Boolean(registrationId) && enabled,
    });
}

/** Display name of a profile (e.g. `rma_approved_by`); null when RLS hides it. */
export function useProfileDisplayName(profileId: string | null | undefined) {
    return useQuery({
        queryKey: ['service-cases', 'profile-name', profileId],
        queryFn: async (): Promise<string | null> => {
            if (!profileId) return null;
            const { data, error } = await supabase
                .from('profiles')
                .select('id, first_name, last_name')
                .eq('id', profileId)
                .maybeSingle();
            if (error) return null;
            const row = data as { first_name?: string | null; last_name?: string | null } | null;
            const name = [row?.first_name, row?.last_name].filter(Boolean).join(' ').trim();
            return name || null;
        },
        enabled: Boolean(profileId),
        staleTime: 5 * 60_000,
    });
}

export interface ManufacturerOption {
    id: string;
    display_name: string;
    logo_url?: string | null;
}

/** Manufacturers visible to the caller, for filters and for naming job-anchored cases. */
export function useManufacturerOptions(enabled = true) {
    return useQuery({
        queryKey: ['service-cases', 'manufacturer-options'],
        queryFn: async (): Promise<ManufacturerOption[]> => {
            const { data, error } = await supabase
                .from('manufacturers')
                .select('id, display_name, logo_url')
                .order('display_name', { ascending: true });
            if (error) throw error;
            return (data ?? []) as ManufacturerOption[];
        },
        enabled,
        staleTime: 5 * 60_000,
    });
}

/** `manufacturer_models.model_code` for a case's `model_id` (no FK, so looked up by id). */
export function useModelCode(modelId: string | null | undefined) {
    return useQuery({
        queryKey: ['service-cases', 'model-code', modelId],
        queryFn: async (): Promise<string | null> => {
            if (!modelId) return null;
            const { data, error } = await supabase
                .from('manufacturer_models')
                .select('id, model_code')
                .eq('id', modelId)
                .maybeSingle();
            if (error) throw error;
            return (data as { model_code?: string } | null)?.model_code ?? null;
        },
        enabled: Boolean(modelId),
        staleTime: 5 * 60_000,
    });
}

/** Pending approvals for the manufacturer menu badge: registrations awaiting verification + cases awaiting a coverage decision. */
export function usePendingApprovalsCount(manufacturerId: string | null | undefined, enabled: boolean) {
    return useQuery({
        queryKey: ['service-cases', 'pending-approvals-count', manufacturerId],
        queryFn: async (): Promise<number> => {
            if (!manufacturerId) return 0;
            const [regs, cases] = await Promise.all([
                supabase
                    .from('install_registrations')
                    .select('id', { count: 'exact', head: true })
                    .eq('manufacturer_id', manufacturerId)
                    .eq('status', 'PENDING_VERIFICATION'),
                supabase
                    .from('service_cases')
                    .select('id', { count: 'exact', head: true })
                    .eq('coverage_decision', 'PENDING')
                    .neq('phase', 'CLOSED'),
            ]);
            if (regs.error) throw regs.error;
            if (cases.error) throw cases.error;
            return (regs.count ?? 0) + (cases.count ?? 0);
        },
        enabled: Boolean(manufacturerId) && enabled,
        staleTime: 60_000,
    });
}

export function useInvalidateServiceCases() {
    const queryClient = useQueryClient();
    return (caseId?: string) => {
        queryClient.invalidateQueries({ queryKey: ['service-cases'] });
        if (caseId) {
            queryClient.invalidateQueries({ queryKey: ['service-cases', 'detail', caseId] });
            queryClient.invalidateQueries({ queryKey: ['service-cases', 'events', caseId] });
        }
    };
}
